import clsx from 'clsx'

import type { SmartListKey, SmartListSummary } from '@/services/crmRelationships'
import ListBar from '../ListBar'
import { SMART_LIST_DOT } from './relationshipFormat'

/** Held in `activeListId` while a smart list is selected, so that no saved
 *  list - "All" included - also draws as selected. */
export const SMART_SELECTION = '__smart__'

/**
 * The left rail.
 *
 * Two kinds of list, both evaluated by the server on every read: the built-in
 * smart lists (stage-based, counted from the relationship rows) and the desk's
 * saved lists (rule-based, exactly as before - same chips, same options menu,
 * same rules editor). Selecting one of either filters the board and the table
 * alike. Counts come from the server, so a chip and the rows it reveals cannot
 * disagree.
 */
export default function SmartListRail({
  lists,
  loading,
  failed,
  onRetry,
  activeSmart,
  onSelectSmart,
  activeListId,
  onSelectList,
  totalCount,
}: {
  lists: SmartListSummary[] | undefined
  loading: boolean
  failed: boolean
  onRetry: () => void
  activeSmart: SmartListKey | null
  onSelectSmart: (key: SmartListKey) => void
  activeListId: string | null
  onSelectList: (id: string | null) => void
  totalCount: number | null
}) {
  return (
    <nav
      aria-label="Lists"
      className="flex w-full flex-none flex-col gap-5 border-b border-amber-400/10 bg-black/25 px-3 py-4 md:w-[196px] md:border-b-0 md:border-r"
    >
      <div>
        <div className="px-2 pb-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-stone-600">
          Lists that update themselves
        </div>
        {failed ? (
          <div role="alert" className="px-2 text-[11px] text-red-300">
            The smart lists could not be read.{' '}
            <button type="button" onClick={onRetry} className="underline hover:text-red-200">
              Retry
            </button>
          </div>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {(lists ?? PLACEHOLDER).map((list) => {
              const active = activeSmart === list.key
              return (
                <li key={list.key}>
                  <button
                    type="button"
                    aria-pressed={active}
                    title={list.hint}
                    disabled={loading}
                    onClick={() => onSelectSmart(list.key)}
                    className={clsx(
                      'flex w-full items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left text-[12px] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
                      active
                        ? 'border-amber-400/30 bg-amber-400/[0.10] font-semibold text-amber-100'
                        : 'border-transparent text-stone-300 hover:border-amber-400/15 hover:bg-amber-400/[0.04]',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className={clsx('h-2 w-2 flex-none rounded-full', SMART_LIST_DOT[list.key])} aria-hidden="true" />
                      <span className="truncate">{list.label}</span>
                    </span>
                    <span className="font-mono text-[10.5px] tabular-nums text-stone-500">
                      {loading ? '…' : list.count}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div>
        <div className="px-2 pb-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-stone-600">Saved lists</div>
        <ListBar
          variant="rail"
          activeListId={activeSmart ? SMART_SELECTION : activeListId}
          onSelectList={onSelectList}
          totalCount={totalCount}
        />
      </div>

      <div className="mt-auto rounded-md border border-white/[0.08] p-3 text-[11px] leading-snug text-stone-500">
        <div className="mb-0.5 font-medium text-stone-300">Internal staff</div>
        Left off the board and out of scoring. They stay in All and under the Internal list.
      </div>
    </nav>
  )
}

const PLACEHOLDER: SmartListSummary[] = [
  { key: 'desk', label: 'On my desk', hint: '', count: 0 },
  { key: 'core', label: 'Core relationships', hint: '', count: 0 },
  { key: 'active', label: 'Active now', hint: '', count: 0 },
  { key: 'cooling', label: 'Going quiet', hint: '', count: 0 },
  { key: 'new', label: 'New this month', hint: '', count: 0 },
  { key: 'confirm', label: 'Unsure entries', hint: '', count: 0 },
]
